import React, { useCallback, useState } from 'react'
import { AIFormStepProvider, useAILoadingContext } from '../AIFormStepContext'
import { useFormStepContext } from 'components/TasksV2/TaskCreation/FormStepContext'
import { PromptGeneratorProvider } from '../PromptGeneratorContext'
import { BaseModalV3 } from 'components/ui/BaseModal'
import { clearTaskCategoriesFromStorage } from 'components/TasksV2/TaskCreation/Steps/SubtaskCreationStep/Widgets/CategorySelector'
import { TaskPublishingOverlay } from 'components/CommonComponents/TaskPublishingOverlay'
import ConfirmationPopup from 'components/CommonComponents/ConfirmationPopup'
import useLocaleText from 'components/useLocaleText'
import { Header } from './Header'
import { Content } from './Content'
import styles from './styles.module.css'

interface IProps {
  onClose: () => void
  isOpen: boolean
  onReset?: () => void
}

export const AIModalContent: React.FC<IProps> = ({ onClose, isOpen, onReset = () => {} }) => {
  const { publicationComplete, isSubmitting, isGenerating } = useAILoadingContext()
  const { isMediaUploading, stepStack } = useFormStepContext()
  const [confirmClose, setConfirmClose] = useState(false)
  const [promptReady, setPromptReady] = useState(false)
  const closeTitle = useLocaleText('card_confirm_text')
  const closeDescription = useLocaleText('card_confirm_desc')
  const yes = useLocaleText('yes')
  const no = useLocaleText('no')
  const [headerSlot, setHeaderSlot] = useState<HTMLDivElement | null>(null)
  const handleClose = () => {
    if (!isSubmitting && !isGenerating && !isMediaUploading) setConfirmClose(true)
  }

  return (
    <>
      <BaseModalV3
        footer={null}
        footerProps={{ style: { display: 'none' } }}
        isOpen={isOpen}
        onClose={handleClose}
        floating
        trapFocus
        closeOnEsc
        closeOnOverlayClick
        header={<Header onClose={handleClose} actionSlot={setHeaderSlot} promptReady={promptReady} />}
        body={
          isSubmitting ? (
            <TaskPublishingOverlay complete={publicationComplete} />
          ) : (
            <Content onReset={onReset} headerSlot={headerSlot} onPromptReadyChange={setPromptReady} />
          )
        }
        headerProps={{ style: { padding: 0, boxShadow: 'none', background: 'transparent' } }}
        bodyProps={{
          style: {
            padding: 0,
            maxHeight: 'none',
            height: 'auto',
            minHeight: 0,
            overflow: 'hidden',
            background: 'transparent',
            borderRadius: 0,
          },
        }}
        contentProps={{ className: styles.modal, 'data-ai-step': stepStack[stepStack.length - 1] } as any}
      />
      <ConfirmationPopup
        isOpen={confirmClose}
        onCancel={() => setConfirmClose(false)}
        onConfirm={onClose}
        isLoading={isSubmitting || isGenerating || isMediaUploading}
        title={closeTitle}
        description={closeDescription}
        confirmButtonText={yes}
        cancelButtonText={no}
      />
    </>
  )
}

export default function AITaskCreationModal({ onClose, isOpen }: IProps) {
  const [session, setSession] = useState(0)
  const handleClose = useCallback(() => {
    clearTaskCategoriesFromStorage()
    onClose()
  }, [onClose])

  return (
    <AIFormStepProvider key={session} onClose={handleClose}>
      <PromptGeneratorProvider>
        <AIModalContent isOpen={isOpen} onClose={handleClose} onReset={() => setSession((value) => value + 1)} />
      </PromptGeneratorProvider>
    </AIFormStepProvider>
  )
}
